import Button from '@rio-cloud/rio-uikit/Button';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import type { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { DriversTableFilterContainer } from '../containers/DriversTableFilterContainer';
import type { DriverTableColumnConfig } from './driverTableColumns';

const SEARCH_DEBOUNCE_MS = 300;

interface DriversTableToolbarProps {
    columns: DriverTableColumnConfig[];
    compactRows: boolean;
    gridLines: boolean;
    onViewTypeChange: (mode: TableViewTogglesViewType) => void;
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    onResetColumns: () => void;
    onToggleCompactRows: () => void;
    onToggleGridLines: () => void;
    driversListMode: TableViewTogglesViewType;
    searchText: string;
    onSearchTextChange: (searchText: string) => void;
    toggleDriverCreationDialog: (show: boolean) => void;
    showCreateButton?: boolean;
}

export const DriversTableToolbar = (props: DriversTableToolbarProps) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();

    const { onViewTypeChange, driversListMode, searchText, onSearchTextChange, showCreateButton = true } = props;

    const [localSearchText, setLocalSearchText] = useState(searchText);
    const [initialRender, setInitialRender] = useState<boolean>(true);

    useEffect(() => {
        setLocalSearchText(searchText);
    }, [searchText]);

    useEffect(() => {
        if (localSearchText === searchText) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            onSearchTextChange(localSearchText);
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [onSearchTextChange, localSearchText, searchText]);

    // set focus on search field when component renders for the first time
    useEffect(() => {
        if (initialRender) {
            searchRef.current?.focus();
            setInitialRender(false);
        }
    }, [initialRender, setInitialRender, searchRef]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const gridLinesFromUrl = searchParams.get('gridLines') === 'true';
        const compactRowsFromUrl = searchParams.get('compactRows') === 'true';

        if (gridLinesFromUrl !== props.gridLines) {
            props.onToggleGridLines();
        }

        if (compactRowsFromUrl !== props.compactRows) {
            props.onToggleCompactRows();
        }
    }, [location.search, props.compactRows, props.gridLines, props.onToggleCompactRows, props.onToggleGridLines]);

    const handleSearchChange = (searchValue: string) => {
        setLocalSearchText(searchValue);
    };

    const updateDisplayOptionInUrl = (key: 'gridLines' | 'compactRows', enabled: boolean) => {
        const searchParams = new URLSearchParams(location.search);

        if (enabled) {
            searchParams.set(key, 'true');
        } else {
            searchParams.delete(key);
        }

        const nextSearch = searchParams.toString();
        const normalizedNextSearch = nextSearch ? `?${nextSearch}` : '';

        if (normalizedNextSearch === location.search) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: normalizedNextSearch,
            },
            { replace: true }
        );
    };

    const handleToggleGridLines = () => {
        const nextGridLines = !props.gridLines;
        props.onToggleGridLines();
        updateDisplayOptionInUrl('gridLines', nextGridLines);
    };

    const handleToggleCompactRows = () => {
        const nextCompactRows = !props.compactRows;
        props.onToggleCompactRows();
        updateDisplayOptionInUrl('compactRows', nextCompactRows);
    };

    const handleCreateDriverClick = () => {
        props.toggleDriverCreationDialog(true);
    };

    const createDriverButton = (
        <div data-testid={'createDriverButton'}>
            <Button
                autoTrackingKey='drivers.create-driver'
                bsStyle={Button.PRIMARY}
                iconName='icon-plus'
                onClick={handleCreateDriverClick}
            >
                <FormattedMessage id={'intl-msg:createDriver.addDriver.button'} />
            </Button>
        </div>
    );

    return (
        <TableToolbar>
            {showCreateButton && <TableToolbarColumn>{createDriverButton}</TableToolbarColumn>}
            <TableToolbarColumn horizontalAlign='right'>
                <DriversTableFilterContainer
                    compactRows={props.compactRows}
                    gridLines={props.gridLines}
                    onToggleCompactRows={handleToggleCompactRows}
                    onToggleGridLines={handleToggleGridLines}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' className='min-width-200' separator>
                <div data-testid='driversTableSearch'>
                    <TableSearch
                        inputRef={searchRef}
                        onChange={handleSearchChange}
                        placeholder={intl.formatMessage({ id: 'intl-msg:searchPlaceholder' })}
                        value={localSearchText}
                    />
                </div>
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableViewToggles
                    onViewTypeChange={onViewTypeChange}
                    viewType={driversListMode}
                    tableViewLabel={<FormattedMessage id='intl-msg:table.tableViewToggles.tableViewLabel' />}
                    singleCardViewLabel={<FormattedMessage id='intl-msg:table.tableViewToggles.singleCardViewLabel' />}
                    multiCardsViewLabel={<FormattedMessage id='intl-msg:table.tableViewToggles.multiCardsViewLabel' />}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableColumnsDropdown
                    columns={props.columns}
                    label={<FormattedMessage id='intl-msg:table.tableColumnsDropdown.label' />}
                    onColumnOrderChange={props.onColumnOrderChange}
                    onColumnVisibilityChange={props.onColumnVisibilityChange}
                    onReset={props.onResetColumns}
                    resetLabel={<FormattedMessage id='intl-msg:table.tableColumnsDropdown.resetLabel' />}
                    showResetButton
                />
            </TableToolbarColumn>
        </TableToolbar>
    );
};
